import { useEffect, useCallback, useRef, useState } from "react";
import { useApi } from "@platform/api";

const cacheTimeInMs = 1000 * 60 * 60; /* 1 hour */
const isExpired = (savedTime) => {
  try {
    const date = new Date(JSON.parse(savedTime));
    const elapsedTime = Date.now() - date.getTime();
    return elapsedTime > cacheTimeInMs;
  } catch {
    return true;
  }
};

function usePostApi(url, onRenderPayload, cacheKey = null, dataPreProcessor) {
  const [state, setState] = useState({
    loading: true,
    data: null,
    statusCode: null,
    error: null,
    success: null,
  });
  const axiosInstance = useApi();

  const updateState = useCallback(
    (newState) => setState((oldState) => ({ ...oldState, ...newState })),
    [],
  );

  // Read through a ref so fetchData keeps a stable identity even when
  // callers pass an inline preprocessor.
  const dataPreProcessorRef = useRef(dataPreProcessor);
  useEffect(() => {
    dataPreProcessorRef.current = dataPreProcessor;
  });

  const fetchData = useCallback(
    async (payload) => {
      try {
        const data = cacheKey && JSON.parse(sessionStorage.getItem(cacheKey));
        updateState({ loading: true });
        if (!!data && !!data?.savedTime && !isExpired(data.savedTime)) {
          updateState({
            error: null,
            data: data?.data,
            loading: false,
          });
        } else {
          const result = await axiosInstance.post(url, payload);
          updateState({
            error: null,
            data: dataPreProcessorRef.current
              ? dataPreProcessorRef.current(result?.data)
              : result?.data,
            statusCode: result?.status,
            loading: false,
            success: true,
          });
          const cacheEntry = {
            data: result?.data,
            savedTime: Date.now(),
          };
          if (cacheKey) {
            sessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
          }
        }
      } catch (e) {
        updateState({
          error: e,
          statusCode: e.response?.status,
          data: null,
          loading: false,
          success: false,
        });
      }
    },
    [cacheKey, url, axiosInstance, updateState],
  );

  // Fire at most once, on mount: onRenderPayload is often an inline object,
  // so depending on its identity would re-post on every render.
  const initialFetchDone = useRef(false);
  useEffect(() => {
    if (initialFetchDone.current) {
      return;
    }
    initialFetchDone.current = true;
    if (onRenderPayload) {
      fetchData(onRenderPayload);
    }
  }, [fetchData, onRenderPayload]);

  const clearError = useCallback(
    () => updateState({ error: null }),
    [updateState],
  );
  const setData = useCallback((data) => updateState({ data }), [updateState]);

  return { ...state, setData, clearError, dispatchCall: fetchData };
}

export default usePostApi;
